import React, { useCallback, useEffect, useState } from "react";
import { shopsAPI } from "../services/api";
import { useI18n } from "../i18n";
import { useMembership } from "../context/MembershipContext";
import { PRODUCT_CATEGORIES, categoryLabel } from "../constants/categories";
import Modal from "./Modal";
import { formatMoney } from "../pos/utils/money";

const PAGE = 50;

/**
 * An owner of several shops copying products from one of their other shops into
 * this one. Names, pictures, details and ranges come along; stock never does (it
 * belongs to the other shop's shelf). Prices are copied unless unticked — then the
 * copies wait as drafts — and buy prices only for someone who may see costs.
 * What is already here is shown and skipped.
 */
const CopyFromShop = ({ sources, currency, onClose, onDone, zClass }) => {
  const { t } = useI18n();
  const { can } = useMembership();
  const [sourceId, setSourceId] = useState(sources[0]?.id || "");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [withPrices, setWithPrices] = useState(true);
  const [withCosts, setWithCosts] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(
    async (offset = 0) => {
      if (!sourceId) return;
      try {
        const { data } = await shopsAPI.copySourceProducts(sourceId, {
          search: search || undefined,
          category: category || undefined,
          limit: PAGE,
          offset,
        });
        setItems((current) => (offset === 0 ? data.items : [...current, ...data.items]));
        setTotal(data.total);
      } catch {
        setMessage({ tone: "error", text: t("shops.copy.failed") });
      }
    },
    [sourceId, search, category, t],
  );

  useEffect(() => {
    const timer = setTimeout(() => load(0), 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => setSelected(new Set()), [sourceId]);

  const toggle = (id) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectPage = () =>
    setSelected((current) => {
      const next = new Set(current);
      items.filter((item) => !item.already_copied).forEach((item) => next.add(item.id));
      return next;
    });

  const copy = async (everything) => {
    setBusy(true);
    setMessage(null);
    try {
      const { data } = await shopsAPI.copyFromShop(sourceId, {
        ...(everything ? { all: true } : { product_ids: [...selected] }),
        with_prices: withPrices,
        with_costs: withCosts,
      });
      setMessage({
        tone: "ok",
        text: [
          t("shops.copy.done", { count: data.created_count }),
          data.skipped_count ? t("shops.copy.skipped", { count: data.skipped_count }) : "",
        ]
          .filter(Boolean)
          .join(" "),
      });
      setSelected(new Set());
      onDone?.();
      load(0);
    } catch (err) {
      setMessage({
        tone: "error",
        text: err.response?.data?.message || err.response?.data?.error || t("shops.copy.failed"),
      });
    } finally {
      setBusy(false);
    }
  };

  const field =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-primary-500 focus:ring-1 focus:ring-primary-500";

  return (
    <Modal title={t("shops.copy.title")} onClose={onClose} maxWidthClass="max-w-2xl" zClass={zClass}>
      <p className="text-sm text-gray-600 mb-4">{t("shops.copy.intro")}</p>

      <div className="grid gap-3 sm:grid-cols-3 mb-3">
        {sources.length > 1 && (
          <label className="block sm:col-span-3">
            <span className="block text-sm font-medium text-gray-700 mb-1">{t("shops.copy.from")}</span>
            <select value={sourceId} onChange={(e) => setSourceId(e.target.value)} className={field}>
              {sources.map((source) => (
                <option key={source.id} value={source.id}>
                  {source.name} ({source.product_count})
                </option>
              ))}
            </select>
          </label>
        )}
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("shops.copy.search")}
          className={`${field} sm:col-span-2`}
        />
        <select value={category} onChange={(e) => setCategory(e.target.value)} className={field}>
          <option value="">{t("shops.copy.all")}</option>
          {PRODUCT_CATEGORIES.map(({ value }) => (
            <option key={value} value={value}>
              {categoryLabel(value)}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2 mb-4">
        <label className="flex items-center gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={withPrices}
            onChange={(e) => setWithPrices(e.target.checked)}
            className="w-5 h-5 text-primary-600 border-gray-300 rounded"
          />
          {t("shops.copy.withPrices")}
        </label>
        {!withPrices && <p className="text-xs text-gray-500 ms-7">{t("shops.copy.withoutPricesHint")}</p>}
        {can("power.see_costs") && (
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={withCosts}
              onChange={(e) => setWithCosts(e.target.checked)}
              className="w-5 h-5 text-primary-600 border-gray-300 rounded"
            />
            {t("shops.copy.withCosts")}
          </label>
        )}
      </div>

      {message && (
        <p
          role="status"
          className={`mb-3 rounded-lg px-3 py-2 text-sm ${
            message.tone === "ok" ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"
          }`}
        >
          {message.text}
        </p>
      )}

      {items.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500">{t("shops.copy.empty")}</p>
      ) : (
        <>
          <button type="button" onClick={selectPage} className="mb-2 text-sm font-semibold text-primary-700 hover:underline">
            {t("shops.copy.selectAll")}
          </button>
          <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 max-h-[45vh] overflow-y-auto">
            {items.map((item) => (
              <li key={item.id}>
                <label
                  className={`flex items-center gap-3 px-3 py-2.5 ${
                    item.already_copied ? "opacity-60" : "cursor-pointer hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    disabled={item.already_copied}
                    checked={item.already_copied || selected.has(item.id)}
                    onChange={() => toggle(item.id)}
                    className="w-5 h-5 text-primary-600 border-gray-300 rounded"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-gray-900 truncate">
                      {item.range_name ? `${item.range_name} · ${item.variant_label}` : item.name}
                    </span>
                    <span className="block text-xs text-gray-500">
                      {[categoryLabel(item.category), item.brand].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs text-gray-600 text-end">
                    {item.already_copied ? (
                      t("shops.copy.alreadyHere")
                    ) : item.is_draft ? (
                      t("shops.copy.draft")
                    ) : (
                      <span dir="ltr">{formatMoney(item.price, currency)}</span>
                    )}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {items.length < total && (
            <button
              type="button"
              onClick={() => load(items.length)}
              className="mt-2 w-full rounded-lg border border-gray-200 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              {t("shops.copy.loadMore")}
            </button>
          )}
        </>
      )}

      <div className="sticky bottom-0 bg-white pt-4 mt-4 border-t flex flex-col sm:flex-row gap-2 sm:justify-end">
        <button
          type="button"
          disabled={busy || total === 0}
          onClick={() => copy(true)}
          className="rounded-lg border border-primary-600 px-4 py-2.5 text-sm font-semibold text-primary-700 hover:bg-primary-50 disabled:opacity-50"
        >
          {t("shops.copy.copyEverything")}
        </button>
        <button
          type="button"
          disabled={busy || selected.size === 0}
          onClick={() => copy(false)}
          className="rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {busy ? t("shops.copy.copying") : t("shops.copy.copy", { count: selected.size })}
        </button>
      </div>
    </Modal>
  );
};

export default CopyFromShop;
